/*
  © 2024–2026 Atlas PHM
  Independently developed software. All rights reserved.
*/

/**
 * Experience Context
 *
 * Manages the three distinct user experiences in Atlas:
 *
 * SHELLS (data input):
 *   1. Customer Shell - Friendly, guided data input with explanations
 *   2. Expert Shell   - Accordion-style fast entry, no hand-holding
 *
 * PORTAL (results output):
 *   3. Customer Portal - Customer-facing report with curated animations & docs
 *
 * The Expert Shell controls which educational content (animations + docs)
 * are visible to the customer in the Portal.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

// ─── Experience Types ───────────────────────────────────────────────

export type ExperienceMode = 'customer-shell' | 'expert-shell' | 'portal';

/**
 * Registry of educational content that can be toggled on/off per report.
 * Each item has a unique key, a human label, a category, and a visibility flag.
 */
export interface EducationalContentItem {
  key: string;
  label: string;
  description: string;
  category: 'animation' | 'document';
  /** Whether this item is visible in the Portal */
  enabled: boolean;
  /** Optional reference to related technical appendix ID */
  appendixId?: string;
}

// ─── Default Content Registry ───────────────────────────────────────

export const DEFAULT_EDUCATIONAL_CONTENT: EducationalContentItem[] = [
  // Animations
  {
    key: 'anim-heat-pump-energy-flow',
    label: 'Heat Pump Energy Flow',
    description: 'Shows how outdoor air, electricity, and useful heat interact in a heat pump system.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-weighing-comparison',
    label: 'Option Weighing Animation',
    description: 'Mechanical balance showing how Atlas compares your heating options.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-y-plan-valve',
    label: 'Y-Plan Valve Operation',
    description: 'Animated valve positions for hot water only, heating only, and mid-position.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-s-plan-valve',
    label: 'S-Plan Valve Operation',
    description: 'Two independent motorised valves controlling heating and hot water circuits.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-sealed-vs-open-vented',
    label: 'Sealed vs Open Vented Systems',
    description: 'Comparison of sealed pressurised and open vented heating layouts.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-hydraulic-separation',
    label: 'Hydraulic Separation',
    description: 'How primary and secondary loops interact through a neutral zone.',
    category: 'animation',
    enabled: true,
  },
  {
    key: 'anim-power-flush',
    label: 'Power Flushing Effect',
    description: 'See how magnetite is removed from radiators with bi-directional high-velocity flow.',
    category: 'animation',
    enabled: true,
    appendixId: 'power-flush-filters',
  },
  {
    key: 'anim-laminar-filter',
    label: 'Laminar Flow Filter',
    description: 'Using physics to spin dirt out of your water — like a bagless vacuum for heating systems.',
    category: 'animation',
    enabled: true,
    appendixId: 'power-flush-filters',
  },
  // Documents
  {
    key: 'doc-boiler-efficiency',
    label: 'Boiler Efficiency Guide',
    description: 'How condensing boilers achieve higher efficiency and what affects performance.',
    category: 'document',
    enabled: true,
    appendixId: 'clean-systems',
  },
  {
    key: 'doc-heat-loss-explained',
    label: 'Heat Loss Explained',
    description: 'Simple guide to understanding heat loss calculations and what they mean for your home.',
    category: 'document',
    enabled: true,
    appendixId: 'pipework-flow',
  },
  {
    key: 'doc-radiator-sizing',
    label: 'Radiator Sizing',
    description: 'Why radiator output matters and how correctly sized radiators save energy.',
    category: 'document',
    enabled: false,
    appendixId: 'pipework-flow',
  },
  {
    key: 'doc-flow-temperatures',
    label: 'Flow Temperature Guide',
    description: 'Why lower flow temperatures improve efficiency and comfort.',
    category: 'document',
    enabled: true,
    appendixId: 'modulation-cycling',
  },
  {
    key: 'doc-controls-thermostats',
    label: 'Controls & Thermostats',
    description: 'Overview of heating controls, programmers, and smart thermostats.',
    category: 'document',
    enabled: true,
    appendixId: 'modulation-cycling',
  },
  {
    key: 'doc-carbon-impact',
    label: 'Carbon Impact Report',
    description: 'How different heating systems compare on carbon emissions.',
    category: 'document',
    enabled: false,
  },
];

// ─── Context Shape ──────────────────────────────────────────────────

interface ExperienceContextValue {
  /** Current experience mode */
  mode: ExperienceMode;
  /** Switch to a different experience */
  setMode: (mode: ExperienceMode) => void;

  /** Full content registry (animations + docs) with enabled/disabled state */
  contentRegistry: EducationalContentItem[];
  /** Toggle a single content item on/off (Expert Shell only) */
  toggleContent: (key: string) => void;
  /** Enable all content items */
  enableAllContent: () => void;
  /** Disable all content items */
  disableAllContent: () => void;
  /** Get only the enabled content items (for Portal rendering) */
  enabledContent: EducationalContentItem[];
  /** Get enabled animations only */
  enabledAnimations: EducationalContentItem[];
  /** Get enabled documents only */
  enabledDocuments: EducationalContentItem[];
}

const ExperienceContext = createContext<ExperienceContextValue | null>(null);

// ─── Provider ───────────────────────────────────────────────────────

interface ExperienceProviderProps {
  children: ReactNode;
  /** Initial mode, defaults to 'customer-shell' */
  initialMode?: ExperienceMode;
}

export function ExperienceProvider({ children, initialMode = 'customer-shell' }: ExperienceProviderProps) {
  const [mode, setMode] = useState<ExperienceMode>(initialMode);
  const [contentRegistry, setContentRegistry] = useState<EducationalContentItem[]>(
    () => DEFAULT_EDUCATIONAL_CONTENT.map(item => ({ ...item }))
  );

  const toggleContent = useCallback((key: string) => {
    setContentRegistry(prev =>
      prev.map(item =>
        item.key === key ? { ...item, enabled: !item.enabled } : item
      )
    );
  }, []);

  const enableAllContent = useCallback(() => {
    setContentRegistry(prev => prev.map(item => ({ ...item, enabled: true })));
  }, []);

  const disableAllContent = useCallback(() => {
    setContentRegistry(prev => prev.map(item => ({ ...item, enabled: false })));
  }, []);

  const enabledContent = contentRegistry.filter(item => item.enabled);
  const enabledAnimations = enabledContent.filter(item => item.category === 'animation');
  const enabledDocuments = enabledContent.filter(item => item.category === 'document');

  return (
    <ExperienceContext.Provider
      value={{
        mode,
        setMode,
        contentRegistry,
        toggleContent,
        enableAllContent,
        disableAllContent,
        enabledContent,
        enabledAnimations,
        enabledDocuments,
      }}
    >
      {children}
    </ExperienceContext.Provider>
  );
}

// ─── Hook ───────────────────────────────────────────────────────────

export function useExperience(): ExperienceContextValue {
  const context = useContext(ExperienceContext);
  if (!context) {
    throw new Error('useExperience must be used within an ExperienceProvider');
  }
  return context;
}
